/*!
 * devextreme-angular
 * Version: 22.2.3
 * Build date: Mon Dec 05 2022
 *
 * Copyright (c) 2012 - 2022 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */

/* tslint:disable:max-line-length */


import { BrowserTransferStateModule } from '@angular/platform-browser';
import { TransferState } from '@angular/platform-browser';

import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    PLATFORM_ID,
    Inject,

    Input,
    Output,
    OnDestroy,
    EventEmitter,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';
import Store from 'devextreme/data/abstract_store';
import DataSource, { Options as DataSourceOptions } from 'devextreme/data/data_source';

import DxDiagram from 'devextreme/ui/diagram';


import {
    DxComponent,
    DxTemplateHost,
    DxIntegrationModule,
    DxTemplateModule,
    NestedOptionHost,
    IterableDifferHelper,
    WatcherHelper
} from 'devextreme-angular/core';

import { DxoContextMenuModule } from 'devextreme-angular/ui/nested';
import { DxiCommandModule } from 'devextreme-angular/ui/nested';
import { DxiItemModule } from 'devextreme-angular/ui/nested';
import { DxoContextToolboxModule } from 'devextreme-angular/ui/nested';
import { DxiCustomShapeModule } from 'devextreme-angular/ui/nested';
import { DxiConnectionPointModule } from 'devextreme-angular/ui/nested';
import { DxoDefaultItemPropertiesModule } from 'devextreme-angular/ui/nested';
import { DxoEdgesModule } from 'devextreme-angular/ui/nested';
import { DxoEditingModule } from 'devextreme-angular/ui/nested';
import { DxoExportModule } from 'devextreme-angular/ui/nested';
import { DxoGridSizeModule } from 'devextreme-angular/ui/nested';
import { DxoHistoryToolbarModule } from 'devextreme-angular/ui/nested';
import { DxoMainToolbarModule } from 'devextreme-angular/ui/nested';
import { DxoNodesModule } from 'devextreme-angular/ui/nested';
import { DxoAutoLayoutModule } from 'devextreme-angular/ui/nested';
import { DxoPageSizeModule } from 'devextreme-angular/ui/nested';
import { DxoPropertiesPanelModule } from 'devextreme-angular/ui/nested';
import { DxiTabModule } from 'devextreme-angular/ui/nested';
import { DxiGroupModule } from 'devextreme-angular/ui/nested';
import { DxoToolboxModule } from 'devextreme-angular/ui/nested';
import { DxoViewToolbarModule } from 'devextreme-angular/ui/nested';
import { DxoZoomLevelModule } from 'devextreme-angular/ui/nested';

import { DxiCustomShapeComponent } from 'devextreme-angular/ui/nested';



/**
 * The Diagram UI component provides a visual interface to help you design new and modify existing diagrams.

 */
@Component({
    selector: 'dx-diagram',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        NestedOptionHost,
        IterableDifferHelper
    ]
})
export class DxDiagramComponent extends DxComponent implements OnDestroy, OnChanges, DoCheck {
    instance: DxDiagram;

    /**
     * Specifies how the Diagram UI component automatically zooms the work area.
    
     */
    @Input()
    get autoZoomMode(): string {
        return this._getOption('autoZoomMode');
    }
    set autoZoomMode(value: string) {
        this._setOption('autoZoomMode', value);
    }


    /**
     * Configures the context menu&apos;s settings.
    
     */
    @Input()
    get contextMenu(): { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, enabled?: boolean } {
        return this._getOption('contextMenu');
    }
    set contextMenu(value: { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, enabled?: boolean }) {
        this._setOption('contextMenu', value);
    }


    /**
     * Configures the context toolbox&apos;s settings.
    
     */
    @Input()
    get contextToolbox(): { category?: string, displayMode?: string, enabled?: boolean, shapeIconsPerRow?: number, shapes?: Array<string>, width?: number } {
        return this._getOption('contextToolbox');
    }
    set contextToolbox(value: { category?: string, displayMode?: string, enabled?: boolean, shapeIconsPerRow?: number, shapes?: Array<string>, width?: number }) {
        this._setOption('contextToolbox', value);
    }


    /**
     * Provide access to an array of custom shapes.
    
     */
    @Input()
    get customShapes(): Array<any | { allowEditImage?: boolean, allowEditText?: boolean, allowResize?: boolean, backgroundImageHeight?: number, backgroundImageLeft?: number, backgroundImageToolboxUrl?: string, backgroundImageTop?: number, backgroundImageUrl?: string, backgroundImageWidth?: number, baseType?: string, category?: string, connectionPoints?: Array<any | { x?: number, y?: number }>, defaultHeight?: number, defaultImageUrl?: string, defaultText?: string, defaultWidth?: number, imageHeight?: number, imageLeft?: number, imageTop?: number, imageWidth?: number, keepRatioOnAutoSize?: boolean, maxHeight?: number, maxWidth?: number, minHeight?: number, minWidth?: number, template?: any, templateHeight?: number, templateLeft?: number, templateTop?: number, templateWidth?: number, textHeight?: number, textLeft?: number, textTop?: number, textWidth?: number, title?: string, toolboxTemplate?: any, toolboxWidthToHeightRatio?: number, type?: string }> {
        return this._getOption('customShapes');
    }
    set customShapes(value: Array<any | { allowEditImage?: boolean, allowEditText?: boolean, allowResize?: boolean, backgroundImageHeight?: number, backgroundImageLeft?: number, backgroundImageToolboxUrl?: string, backgroundImageTop?: number, backgroundImageUrl?: string, backgroundImageWidth?: number, baseType?: string, category?: string, connectionPoints?: Array<any | { x?: number, y?: number }>, defaultHeight?: number, defaultImageUrl?: string, defaultText?: string, defaultWidth?: number, imageHeight?: number, imageLeft?: number, imageTop?: number, imageWidth?: number, keepRatioOnAutoSize?: boolean, maxHeight?: number, maxWidth?: number, minHeight?: number, minWidth?: number, template?: any, templateHeight?: number, templateLeft?: number, templateTop?: number, templateWidth?: number, textHeight?: number, textLeft?: number, textTop?: number, textWidth?: number, title?: string, toolboxTemplate?: any, toolboxWidthToHeightRatio?: number, type?: string }>) {
        this._setOption('customShapes', value);
    }


    /**
     * Specifies a custom template for shapes.
    
     */
    @Input()
    get customShapeTemplate(): any {
        return this._getOption('customShapeTemplate');
    }
    set customShapeTemplate(value: any) {
        this._setOption('customShapeTemplate', value);
    }


    /**
     * Specifies a custom template for shapes in the toolbox.
    
     */
    @Input()
    get customShapeToolboxTemplate(): any {
        return this._getOption('customShapeToolboxTemplate');
    }
    set customShapeToolboxTemplate(value: any) {
        this._setOption('customShapeToolboxTemplate', value);
    }


    /**
     * Configures default item properties.
    
     */
    @Input()
    get defaultItemProperties(): { connectorLineEnd?: string, connectorLineStart?: string, connectorLineType?: string, shapeMaxHeight?: number | undefined, shapeMaxWidth?: number | undefined, shapeMinHeight?: number | undefined, shapeMinWidth?: number | undefined, style?: any, textStyle?: any } {
        return this._getOption('defaultItemProperties');
    }
    set defaultItemProperties(value: { connectorLineEnd?: string, connectorLineStart?: string, connectorLineType?: string, shapeMaxHeight?: number | undefined, shapeMaxWidth?: number | undefined, shapeMinHeight?: number | undefined, shapeMinWidth?: number | undefined, style?: any, textStyle?: any }) {
        this._setOption('defaultItemProperties', value);
    }


    /**
     * Specifies whether the UI component responds to user interaction.
    
     */
    @Input()
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Allows you to bind the collection of diagram edges to a data source. For more information, see the Data Binding section.
    
     */
    @Input()
    get edges(): { customDataExpr?: Function | string | undefined, dataSource?: DataSource | DataSourceOptions | Store | null | string | Array<any>, fromExpr?: Function | string, fromLineEndExpr?: Function | string | undefined, fromPointIndexExpr?: Function | string | undefined, keyExpr?: Function | string, lineTypeExpr?: Function | string | undefined, lockedExpr?: Function | string | undefined, pointsExpr?: Function | string | undefined, styleExpr?: Function | string | undefined, textExpr?: Function | string | undefined, textStyleExpr?: Function | string | undefined, toExpr?: Function | string, toLineEndExpr?: Function | string | undefined, toPointIndexExpr?: Function | string | undefined, zIndexExpr?: Function | string | undefined } {
        return this._getOption('edges');
    }
    set edges(value: { customDataExpr?: Function | string | undefined, dataSource?: DataSource | DataSourceOptions | Store | null | string | Array<any>, fromExpr?: Function | string, fromLineEndExpr?: Function | string | undefined, fromPointIndexExpr?: Function | string | undefined, keyExpr?: Function | string, lineTypeExpr?: Function | string | undefined, lockedExpr?: Function | string | undefined, pointsExpr?: Function | string | undefined, styleExpr?: Function | string | undefined, textExpr?: Function | string | undefined, textStyleExpr?: Function | string | undefined, toExpr?: Function | string, toLineEndExpr?: Function | string | undefined, toPointIndexExpr?: Function | string | undefined, zIndexExpr?: Function | string | undefined }) {
        this._setOption('edges', value);
    }


    /**
     * Specifies which editing operations a user can perform.
    
     */
    @Input()
    get editing(): { allowAddShape?: boolean, allowChangeConnection?: boolean, allowChangeConnectorPoints?: boolean, allowChangeConnectorText?: boolean, allowChangeShapeText?: boolean, allowDeleteConnector?: boolean, allowDeleteShape?: boolean, allowMoveShape?: boolean, allowResizeShape?: boolean } {
        return this._getOption('editing');
    }
    set editing(value: { allowAddShape?: boolean, allowChangeConnection?: boolean, allowChangeConnectorPoints?: boolean, allowChangeConnectorText?: boolean, allowChangeShapeText?: boolean, allowDeleteConnector?: boolean, allowDeleteShape?: boolean, allowMoveShape?: boolean, allowResizeShape?: boolean }) {
        this._setOption('editing', value);
    }


    /**
     * Specifies the global attributes to be attached to the UI component&apos;s container element.
    
     */
    @Input()
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Configures export settings.
    
     */
    @Input()
    get export(): { fileName?: string } {
        return this._getOption('export');
    }
    set export(value: { fileName?: string }) {
        this._setOption('export', value);
    }


    /**
     * Specifies whether or not to display the UI component in full-screen mode.
    
     */
    @Input()
    get fullScreen(): boolean {
        return this._getOption('fullScreen');
    }
    set fullScreen(value: boolean) {
        this._setOption('fullScreen', value);
    }


    /**
     * Specifies the grid pitch.
    
     */
    @Input()
    get gridSize(): number | { items?: Array<number>, value?: number } {
        return this._getOption('gridSize');
    }
    set gridSize(value: number | { items?: Array<number>, value?: number }) {
        this._setOption('gridSize', value);
    }


    /**
     * Indicates whether diagram content has unsaved changes.
    
     */
    @Input()
    get hasChanges(): boolean {
        return this._getOption('hasChanges');
    }
    set hasChanges(value: boolean) {
        this._setOption('hasChanges', value);
    }


    /**
     * Specifies the UI component&apos;s height.
    
     */
    @Input()
    get height(): number | Function | string | undefined {
        return this._getOption('height');
    }
    set height(value: number | Function | string | undefined) {
        this._setOption('height', value);
    }


    /**
     * Configures the history toolbar&apos;s settings.
    
     */
    @Input()
    get historyToolbar(): { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, visible?: boolean } {
        return this._getOption('historyToolbar');
    }
    set historyToolbar(value: { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, visible?: boolean }) {
        this._setOption('historyToolbar', value);
    }


    /**
     * Configures the main toolbar settings.
    
     */
    @Input()
    get mainToolbar(): { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, visible?: boolean } {
        return this._getOption('mainToolbar');
    }
    set mainToolbar(value: { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, visible?: boolean }) {
        this._setOption('mainToolbar', value);
    }


    /**
     * Allows you to bind the collection of diagram nodes to a data source. For more information, see the Data Binding section.
    
     */
    @Input()
    get nodes(): { autoLayout?: string | { orientation?: string, type?: string }, autoSizeEnabled?: boolean, containerChildrenExpr?: Function | string | undefined, containerKeyExpr?: Function | string, customDataExpr?: Function | string | undefined, dataSource?: DataSource | DataSourceOptions | Store | null | string | Array<any>, heightExpr?: Function | string | undefined, imageUrlExpr?: Function | string | undefined, itemsExpr?: Function | string | undefined, keyExpr?: Function | string, leftExpr?: Function | string | undefined, lockedExpr?: Function | string | undefined, parentKeyExpr?: Function | string | undefined, styleExpr?: Function | string | undefined, textExpr?: Function | string, textStyleExpr?: Function | string | undefined, topExpr?: Function | string | undefined, typeExpr?: Function | string, widthExpr?: Function | string | undefined, zIndexExpr?: Function | string | undefined } {
        return this._getOption('nodes');
    }
    set nodes(value: { autoLayout?: string | { orientation?: string, type?: string }, autoSizeEnabled?: boolean, containerChildrenExpr?: Function | string | undefined, containerKeyExpr?: Function | string, customDataExpr?: Function | string | undefined, dataSource?: DataSource | DataSourceOptions | Store | null | string | Array<any>, heightExpr?: Function | string | undefined, imageUrlExpr?: Function | string | undefined, itemsExpr?: Function | string | undefined, keyExpr?: Function | string, leftExpr?: Function | string | undefined, lockedExpr?: Function | string | undefined, parentKeyExpr?: Function | string | undefined, styleExpr?: Function | string | undefined, textExpr?: Function | string, textStyleExpr?: Function | string | undefined, topExpr?: Function | string | undefined, typeExpr?: Function | string, widthExpr?: Function | string | undefined, zIndexExpr?: Function | string | undefined }) {
        this._setOption('nodes', value);
    }


    /**
     * Specifies the color of a diagram page.
    
     */
    @Input()
    get pageColor(): string {
        return this._getOption('pageColor');
    }
    set pageColor(value: string) {
        this._setOption('pageColor', value);
    }


    /**
     * Specifies the page orientation.
    
     */
    @Input()
    get pageOrientation(): string {
        return this._getOption('pageOrientation');
    }
    set pageOrientation(value: string) {
        this._setOption('pageOrientation', value);
    }


    /**
     * Specifies a size of pages.
    
     */
    @Input()
    get pageSize(): { height?: number, items?: Array<any | { height?: number, text?: string, width?: number }>, width?: number } {
        return this._getOption('pageSize');
    }
    set pageSize(value: { height?: number, items?: Array<any | { height?: number, text?: string, width?: number }>, width?: number }) {
        this._setOption('pageSize', value);
    }


    /**
     * Configures the Properties panel settings.
    
     */
    @Input()
    get propertiesPanel(): { tabs?: Array<any | { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, groups?: Array<any | { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, title?: string }>, title?: string }>, visibility?: string } {
        return this._getOption('propertiesPanel');
    }
    set propertiesPanel(value: { tabs?: Array<any | { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, groups?: Array<any | { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, title?: string }>, title?: string }>, visibility?: string }) {
        this._setOption('propertiesPanel', value);
    }


    /**
     * Specifies whether the diagram is read-only.
    
     */
    @Input()
    get readOnly(): boolean {
        return this._getOption('readOnly');
    }
    set readOnly(value: boolean) {
        this._setOption('readOnly', value);
    }


    /**
     * Switches the UI component to a right-to-left representation.
    
     */
    @Input()
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Specifies whether grid lines are visible.
    
     */
    @Input()
    get showGrid(): boolean {
        return this._getOption('showGrid');
    }
    set showGrid(value: boolean) {
        this._setOption('showGrid', value);
    }


    /**
     * Switch the Diagram UI component to simple view mode.
    
     */
    @Input()
    get simpleView(): boolean {
        return this._getOption('simpleView');
    }
    set simpleView(value: boolean) {
        this._setOption('simpleView', value);
    }


    /**
     * Specifies whether diagram elements should snap to grid lines.
    
     */
    @Input()
    get snapToGrid(): boolean {
        return this._getOption('snapToGrid');
    }
    set snapToGrid(value: boolean) {
        this._setOption('snapToGrid', value);
    }


    /**
     * Configures the toolbox settings.
    
     */
    @Input()
    get toolbox(): { groups?: Array<any | string | { category?: string, displayMode?: string, expanded?: boolean, shapes?: Array<string>, title?: string }>, shapeIconsPerRow?: number, showSearch?: boolean, visibility?: string, width?: number | undefined } {
        return this._getOption('toolbox');
    }
    set toolbox(value: { groups?: Array<any | string | { category?: string, displayMode?: string, expanded?: boolean, shapes?: Array<string>, title?: string }>, shapeIconsPerRow?: number, showSearch?: boolean, visibility?: string, width?: number | undefined }) {
        this._setOption('toolbox', value);
    }


    /**
     * Specifies the measurement unit for size properties.
    
     */
    @Input()
    get units(): string {
        return this._getOption('units');
    }
    set units(value: string) {
        this._setOption('units', value);
    }


    /**
     * Specifies whether or not the UI component uses native scrolling.
    
     */
    @Input()
    get useNativeScrolling(): boolean {
        return this._getOption('useNativeScrolling');
    }
    set useNativeScrolling(value: boolean) {
        this._setOption('useNativeScrolling', value);
    }


    /**
     * Configures the view toolbar settings.
    
     */
    @Input()
    get viewToolbar(): { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, visible?: boolean } {
        return this._getOption('viewToolbar');
    }
    set viewToolbar(value: { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, visible?: boolean }) {
        this._setOption('viewToolbar', value);
    }


    /**
     * Specifies the measurement unit that is displayed in user interface elements.
    
     */
    @Input()
    get viewUnits(): string {
        return this._getOption('viewUnits');
    }
    set viewUnits(value: string) {
        this._setOption('viewUnits', value);
    }


    /**
     * Specifies whether the UI component is visible.
    
     */
    @Input()
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the UI component&apos;s width.
    
     */
    @Input()
    get width(): number | Function | string | undefined {
        return this._getOption('width');
    }
    set width(value: number | Function | string | undefined) {
        this._setOption('width', value);
    }


    /**
     * Specifies the zoom level.
    
     */
    @Input()
    get zoomLevel(): number | { items?: Array<number>, value?: number | undefined } {
        return this._getOption('zoomLevel');
    }
    set zoomLevel(value: number | { items?: Array<number>, value?: number | undefined }) {
        this._setOption('zoomLevel', value);
    }

    /**
    
     * A function that is executed when the UI component is rendered and each time the component is repainted.
    
    
     */
    @Output() onContentReady: EventEmitter<any>;

    /**
    
     * A function that is executed after a custom command item was clicked and allows you to implement the custom command&apos;s logic.
    
    
     */
    @Output() onCustomCommand: EventEmitter<any>;

    /**
    
     * A function that is executed before the UI component is disposed of.
    
    
     */
    @Output() onDisposing: EventEmitter<any>;

    /**
    
     * A function used in JavaScript frameworks to save the UI component instance.
    
    
     */
    @Output() onInitialized: EventEmitter<any>;

    /**
    
     * A function that is executed after a shape or connector is clicked.
    
    
     */
    @Output() onItemClick: EventEmitter<any>;

    /**
    
     * A function that is executed after a shape or connector is double-clicked.
    
    
     */
    @Output() onItemDblClick: EventEmitter<any>;

    /**
    
     * A function that is executed after a UI component property is changed.
    
    
     */
    @Output() onOptionChanged: EventEmitter<any>;

    /**
    
     * A function that allows you to prohibit an edit operation at run time.
    
    
     */
    @Output() onRequestEditOperation: EventEmitter<any>;

    /**
    
     * A function that is executed after diagram data is reloaded and allows you to specify whether or not the UI component should update the diagram layout.
    
    
     */
    @Output() onRequestLayoutUpdate: EventEmitter<any>;

    /**
    
     * A function that is executed after the selection is changed in the Diagram.
    
    
     */
    @Output() onSelectionChanged: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() autoZoomModeChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() contextMenuChange: EventEmitter<{ commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, enabled?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() contextToolboxChange: EventEmitter<{ category?: string, displayMode?: string, enabled?: boolean, shapeIconsPerRow?: number, shapes?: Array<string>, width?: number }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() customShapesChange: EventEmitter<Array<any | { allowEditImage?: boolean, allowEditText?: boolean, allowResize?: boolean, backgroundImageHeight?: number, backgroundImageLeft?: number, backgroundImageToolboxUrl?: string, backgroundImageTop?: number, backgroundImageUrl?: string, backgroundImageWidth?: number, baseType?: string, category?: string, connectionPoints?: Array<any | { x?: number, y?: number }>, defaultHeight?: number, defaultImageUrl?: string, defaultText?: string, defaultWidth?: number, imageHeight?: number, imageLeft?: number, imageTop?: number, imageWidth?: number, keepRatioOnAutoSize?: boolean, maxHeight?: number, maxWidth?: number, minHeight?: number, minWidth?: number, template?: any, templateHeight?: number, templateLeft?: number, templateTop?: number, templateWidth?: number, textHeight?: number, textLeft?: number, textTop?: number, textWidth?: number, title?: string, toolboxTemplate?: any, toolboxWidthToHeightRatio?: number, type?: string }>>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() customShapeTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() customShapeToolboxTemplateChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() defaultItemPropertiesChange: EventEmitter<{ connectorLineEnd?: string, connectorLineStart?: string, connectorLineType?: string, shapeMaxHeight?: number | undefined, shapeMaxWidth?: number | undefined, shapeMinHeight?: number | undefined, shapeMinWidth?: number | undefined, style?: any, textStyle?: any }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() disabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() edgesChange: EventEmitter<{ customDataExpr?: Function | string | undefined, dataSource?: DataSource | DataSourceOptions | Store | null | string | Array<any>, fromExpr?: Function | string, fromLineEndExpr?: Function | string | undefined, fromPointIndexExpr?: Function | string | undefined, keyExpr?: Function | string, lineTypeExpr?: Function | string | undefined, lockedExpr?: Function | string | undefined, pointsExpr?: Function | string | undefined, styleExpr?: Function | string | undefined, textExpr?: Function | string | undefined, textStyleExpr?: Function | string | undefined, toExpr?: Function | string, toLineEndExpr?: Function | string | undefined, toPointIndexExpr?: Function | string | undefined, zIndexExpr?: Function | string | undefined }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() editingChange: EventEmitter<{ allowAddShape?: boolean, allowChangeConnection?: boolean, allowChangeConnectorPoints?: boolean, allowChangeConnectorText?: boolean, allowChangeShapeText?: boolean, allowDeleteConnector?: boolean, allowDeleteShape?: boolean, allowMoveShape?: boolean, allowResizeShape?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() elementAttrChange: EventEmitter<any>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() exportChange: EventEmitter<{ fileName?: string }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() fullScreenChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() gridSizeChange: EventEmitter<number | { items?: Array<number>, value?: number }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() hasChangesChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() heightChange: EventEmitter<number | Function | string | undefined>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() historyToolbarChange: EventEmitter<{ commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, visible?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() mainToolbarChange: EventEmitter<{ commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, visible?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() nodesChange: EventEmitter<{ autoLayout?: string | { orientation?: string, type?: string }, autoSizeEnabled?: boolean, containerChildrenExpr?: Function | string | undefined, containerKeyExpr?: Function | string, customDataExpr?: Function | string | undefined, dataSource?: DataSource | DataSourceOptions | Store | null | string | Array<any>, heightExpr?: Function | string | undefined, imageUrlExpr?: Function | string | undefined, itemsExpr?: Function | string | undefined, keyExpr?: Function | string, leftExpr?: Function | string | undefined, lockedExpr?: Function | string | undefined, parentKeyExpr?: Function | string | undefined, styleExpr?: Function | string | undefined, textExpr?: Function | string, textStyleExpr?: Function | string | undefined, topExpr?: Function | string | undefined, typeExpr?: Function | string, widthExpr?: Function | string | undefined, zIndexExpr?: Function | string | undefined }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() pageColorChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() pageOrientationChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() pageSizeChange: EventEmitter<{ height?: number, items?: Array<any | { height?: number, text?: string, width?: number }>, width?: number }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() propertiesPanelChange: EventEmitter<{ tabs?: Array<any | { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, groups?: Array<any | { commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, title?: string }>, title?: string }>, visibility?: string }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() readOnlyChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() rtlEnabledChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() showGridChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() simpleViewChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() snapToGridChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() toolboxChange: EventEmitter<{ groups?: Array<any | string | { category?: string, displayMode?: string, expanded?: boolean, shapes?: Array<string>, title?: string }>, shapeIconsPerRow?: number, showSearch?: boolean, visibility?: string, width?: number | undefined }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() unitsChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() useNativeScrollingChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() viewToolbarChange: EventEmitter<{ commands?: Array<DevExpress.ui.dxDiagramCustomCommand | string>, visible?: boolean }>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() viewUnitsChange: EventEmitter<string>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() visibleChange: EventEmitter<boolean>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() widthChange: EventEmitter<number | Function | string | undefined>;

    /**
    
     * This member supports the internal infrastructure and is not intended to be used directly from your code.
    
     */
    @Output() zoomLevelChange: EventEmitter<number | { items?: Array<number>, value?: number | undefined }>;




    @ContentChildren(DxiCustomShapeComponent)
    get customShapesChildren(): QueryList<DxiCustomShapeComponent> {
        return this._getOption('customShapes');
    }
    set customShapesChildren(value) {
        this.setChildren('customShapes', value);
    }




    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper,
            optionHost: NestedOptionHost,
            transferState: TransferState,
            @Inject(PLATFORM_ID) platformId: any) {

        super(elementRef, ngZone, templateHost, _watcherHelper, transferState, platformId);

        this._createEventEmitters([
            { subscribe: 'contentReady', emit: 'onContentReady' },
            { subscribe: 'customCommand', emit: 'onCustomCommand' },
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'itemClick', emit: 'onItemClick' },
            { subscribe: 'itemDblClick', emit: 'onItemDblClick' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'requestEditOperation', emit: 'onRequestEditOperation' },
            { subscribe: 'requestLayoutUpdate', emit: 'onRequestLayoutUpdate' },
            { subscribe: 'selectionChanged', emit: 'onSelectionChanged' },
            { emit: 'autoZoomModeChange' },
            { emit: 'contextMenuChange' },
            { emit: 'contextToolboxChange' },
            { emit: 'customShapesChange' },
            { emit: 'customShapeTemplateChange' },
            { emit: 'customShapeToolboxTemplateChange' },
            { emit: 'defaultItemPropertiesChange' },
            { emit: 'disabledChange' },
            { emit: 'edgesChange' },
            { emit: 'editingChange' },
            { emit: 'elementAttrChange' },
            { emit: 'exportChange' },
            { emit: 'fullScreenChange' },
            { emit: 'gridSizeChange' },
            { emit: 'hasChangesChange' },
            { emit: 'heightChange' },
            { emit: 'historyToolbarChange' },
            { emit: 'mainToolbarChange' },
            { emit: 'nodesChange' },
            { emit: 'pageColorChange' },
            { emit: 'pageOrientationChange' },
            { emit: 'pageSizeChange' },
            { emit: 'propertiesPanelChange' },
            { emit: 'readOnlyChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'showGridChange' },
            { emit: 'simpleViewChange' },
            { emit: 'snapToGridChange' },
            { emit: 'toolboxChange' },
            { emit: 'unitsChange' },
            { emit: 'useNativeScrollingChange' },
            { emit: 'viewToolbarChange' },
            { emit: 'viewUnitsChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' },
            { emit: 'zoomLevelChange' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {

        return new DxDiagram(element, options);
    }


    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('customShapes', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('customShapes');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
        super.clearChangedOptions();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }
}

@NgModule({
  imports: [
    DxoContextMenuModule,
    DxiCommandModule,
    DxiItemModule,
    DxoContextToolboxModule,
    DxiCustomShapeModule,
    DxiConnectionPointModule,
    DxoDefaultItemPropertiesModule,
    DxoEdgesModule,
    DxoEditingModule,
    DxoExportModule,
    DxoGridSizeModule,
    DxoHistoryToolbarModule,
    DxoMainToolbarModule,
    DxoNodesModule,
    DxoAutoLayoutModule,
    DxoPageSizeModule,
    DxoPropertiesPanelModule,
    DxiTabModule,
    DxiGroupModule,
    DxoToolboxModule,
    DxoViewToolbarModule,
    DxoZoomLevelModule,
    DxIntegrationModule,
    DxTemplateModule,
    BrowserTransferStateModule
  ],
  declarations: [
    DxDiagramComponent
  ],
  exports: [
    DxDiagramComponent,
    DxoContextMenuModule,
    DxiCommandModule,
    DxiItemModule,
    DxoContextToolboxModule,
    DxiCustomShapeModule,
    DxiConnectionPointModule,
    DxoDefaultItemPropertiesModule,
    DxoEdgesModule,
    DxoEditingModule,
    DxoExportModule,
    DxoGridSizeModule,
    DxoHistoryToolbarModule,
    DxoMainToolbarModule,
    DxoNodesModule,
    DxoAutoLayoutModule,
    DxoPageSizeModule,
    DxoPropertiesPanelModule,
    DxiTabModule,
    DxiGroupModule,
    DxoToolboxModule,
    DxoViewToolbarModule,
    DxoZoomLevelModule,
    DxTemplateModule
  ]
})
export class DxDiagramModule { }
